import React, { useState, useEffect, useRef, ReactElement } from 'react';
import {
  Modal,
  Button,
  Alert,
  EditIcon,
  FileIcon,
  Paragraph,
  FormItem,
  Input,
  Fieldset,
  FileUpload,
  DeleteIcon,
  UploadOutlinedIcon,
  Checkbox,
  Link,
  TextArea,
} from 'elmo-elements';
import { AiSparkleIcon } from '@/components/Icons';
import PickerCard, { PickerCardOption } from '@/components/PickerCard';
import AiPoweredBadge from '@/components/AiPoweredBadge';
import { ImportCapacity } from '@/modules/services/pptImport';
import {
  FileCheckStatus,
  PromptCheckStatus,
} from '@/modules/services/aiCreate';
import { AiValidationView, fileKey } from './useAiCreateSubmit';
import { CREATION_METHODS, CreationMethod } from './index';

const ACCEPTABLE_USE_URL = 'https://elmosoftware.com.au/ai-terms-conditions';
// Placeholder until the "What makes a good prompt?" Help Centre article is authored.
const PROMPT_HELP_URL = 'https://elmosoftware.com.au/';

const MAX_NAME_LENGTH = 255;
const MAX_FILE_SIZE_MB = 30;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;
const MAX_SLIDES = 60;
const PPTX_MIME =
  'application/vnd.openxmlformats-officedocument.presentationml.presentation';

const MAX_CONTEXT_FILES = 2;
const CONTEXT_FILE_TYPES = ['.pdf', '.pptx', '.docx'];
const CONTEXT_FILE_TYPE_LABEL = 'PDF, PPTX or DOCX';
// Functional Spec B11, 20 pages agreed in LMS2-5216: PDF/PPTX page limit, checked by the API
// on Create (page count needs the file opened). DOCX is limited by text instead.
const MAX_CONTEXT_FILE_PAGES = 20;
// Both context files travel in one multipart request behind a 32MB nginx body
// limit (cobra `client_max_body_size`), so the pair is capped as a whole too.
const MAX_TOTAL_CONTEXT_MB = 30;
const MAX_TOTAL_CONTEXT_BYTES = MAX_TOTAL_CONTEXT_MB * 1024 * 1024;

const hasExtension = (file: File, extensions: string[]) =>
  extensions.some((extension) => file.name.toLowerCase().endsWith(extension));

// LMS2-5096 validation copy, taken from the Create with AI wireframes referenced in
// LMS2-5095 "Visual context" (frames B2, B4, B5, B10). Deliberately silent on *why*
// a guardrail fired (Rules 8, 12). B10 is also fixed verbatim in the Functional Spec.
// `out_of_scope` (LMS2-5097, spec B9) has no wireframe text yet — agreed draft, 16 Sep 2026.
const PROMPT_MESSAGES: Record<Exclude<PromptCheckStatus, 'ok'>, string> = {
  too_short:
    'Add a bit more detail before we can draft anything — a sentence or two naming the audience and what they should be able to do afterwards.',
  blocked:
    "We can't generate from this prompt. Some of the content in your description isn't supported — revise it and try again.",
  out_of_scope:
    "We can't generate from this prompt. It doesn't describe a workplace learning or development topic — revise it to focus on training your staff need, and try again.",
};

// LMS2-5098 copy (PO, 24 Sep 2026): a relevance flag is a warning (amber) with no button —
// clicking Create again is the "use it anyway"; deleting the file is the other way out.
/**
 * The second sentence names the second Create click as the override, so it is only
 * shown while that is true: once the prompt is edited the next click re-judges the
 * file against the new prompt instead, and promising an override it will not give
 * costs the admin a click.
 */
const notRelevantMessage = (fileName: string, overrideOnNextClick: boolean) => {
  const next = overrideOnNextClick
    ? 'Click Create again to use it anyway, or delete it before resubmitting the request.'
    : 'Delete it, or click Create to check it against your updated prompt.';
  return `${fileName} couldn't be used as it doesn't look related to your topic. ${next}`;
};

type FileRowTone = 'danger' | 'warning';

/** Inline "!" glyph so the row does not depend on the icon set. */
const ToneIcon = ({ tone }: { tone: FileRowTone }): ReactElement | null =>
  tone === 'danger' ? null : (
    <svg className="file-row__icon" viewBox="0 0 16 16" aria-hidden="true">
      <circle
        cx="8"
        cy="8"
        r="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M8 4.5v4M8 11h.01"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );

const FILE_MESSAGES: Record<
  Exclude<FileCheckStatus, 'ok' | 'not_relevant'>,
  string
> = {
  blocked:
    'This file cannot be used as its content does not adhere to our acceptable use policy. Delete the file before resubmitting the request.',
  unreadable:
    'This file cannot be used due to an error in processing. Delete the file before resubmitting the request.',
  // Spec B11: unlike B10 this one states the limit — the admin can act on it precisely.
  too_long: `This file is over the ${MAX_CONTEXT_FILE_PAGES}-page limit. Upload a shorter version or an extract.`,
  // LMS2-5216: a DOCX over its text limit, or the longer of two files that together have too much text.
  too_much_text:
    'This file has too much text. Upload a shorter version or an extract.',
  // LMS2-5141: its images would need more memory to render than the worker has.
  too_complex:
    'This file has too many large images to process. Reduce or compress the images and upload it again.',
};

const TARGET_LENGTHS = [
  { value: 'short', label: 'Short', hint: 'Under 10 slides' },
  { value: 'medium', label: 'Medium', hint: '10\u201320 slides' },
  { value: 'long', label: 'Long', hint: '20\u201335 slides' },
] as const;

export type TargetLength = (typeof TARGET_LENGTHS)[number]['value'];

/**
 * Strips HTML tags from pasted or typed prompt text before it is submitted.
 * Scans rather than using /<[^>]*>/g: that regex backtracks to end-of-string
 * from every unclosed '<', which is quadratic and runs on each keystroke.
 */
const stripHtml = (value: string) => {
  let out = '';
  let i = 0;
  while (i < value.length) {
    const lt = value.indexOf('<', i);
    if (lt === -1) return out + value.slice(i);
    const gt = value.indexOf('>', lt + 1);
    if (gt === -1) return out + value.slice(i);
    out += value.slice(i, lt);
    i = gt + 1;
  }
  return out;
};

const fileExtension = (filename: string) =>
  filename.slice(filename.lastIndexOf('.') + 1).toLowerCase();

const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024
    ? `${(bytes / 1024).toFixed(0)} KB`
    : `${(bytes / 1024 / 1024).toFixed(1)} MB`;

/** Shared by the PPT upload and the Create with AI context file list. */
const FileRow = ({
  file,
  onRemove,
  removeLabel = 'Remove file',
  message,
  tone = 'danger',
  isRemoveDisabled = false,
}: {
  file: File;
  onRemove: () => void;
  removeLabel?: string;
  /** Validation message for this file (LMS2-5096); the trash icon still deletes (LMS2-5093). */
  message?: string;
  /** Red for a hard failure, amber for a relevance flag (LMS2-5098). */
  tone?: FileRowTone;
  /** While a check is in flight the list is frozen, so a late result can't resurrect a deleted file. */
  isRemoveDisabled?: boolean;
}): ReactElement => {
  const extension = fileExtension(file.name);
  return (
    <div
      className={`file-row${
        message ? ` file-row--flagged file-row--${tone}` : ''
      }`}
    >
      <div className={`file-row__badge file-row__badge--${extension}`}>
        {extension.toUpperCase()}
      </div>
      <div className="file-row__info">
        <span className="file-row__name">{file.name}</span>
        <span className="file-row__size">{formatFileSize(file.size)}</span>
      </div>
      <button
        type="button"
        className="file-row__remove"
        aria-label={removeLabel}
        onClick={onRemove}
        disabled={isRemoveDisabled}
      >
        <DeleteIcon />
      </button>
      {message && (
        <div className="file-row__message" role="alert">
          <ToneIcon tone={tone} />
          <span>{message}</span>
        </div>
      )}
    </div>
  );
};

/** The AI setup fields as last submitted, so a failed generation can send the
 *  admin back to a form that still holds what they typed. */
export interface AiSetupValues {
  name: string;
  prompt: string;
  targetLength: TargetLength;
  contextFiles: File[];
}

export interface CreationMethodModalProps {
  isOpened: boolean;
  /** Seeds the AI card when returning from a failed generation. */
  initialAiValues?: AiSetupValues | null;
  onClose: () => void;
  onManualCreate: (name: string) => void;
  onPptImport: (data: { name: string; file: File }) => void;
  onAiCreate: (data: {
    name: string;
    prompt: string;
    targetLength: TargetLength;
    contextFiles: File[];
    /** Relevance-flagged files the admin is overriding with this click (LMS2-5098). */
    acceptContextFiles: File[];
  }) => void;
  /**
   * Verdicts for the current click (LMS2-5096); undefined before any click. One
   * call now both checks and starts (LMS2-5164), so `isValidating` covers the whole
   * action and `submitError` carries every non-verdict failure.
   */
  aiValidation?: AiValidationView;
  /** Lets the owner clear a deleted file's message so Create is not left disabled. */
  onContextFileRemoved?: (file: File) => void;
  /** Card switch clears the AI form, so the owner must drop its validation state too. */
  onAiValidationReset?: () => void;
  isPptConversionEnabled: boolean;
  isCreateWithAiEnabled: boolean;
  isCreateLoading: boolean;
  isPptImporting: boolean;
  createError: string | null;
  pptError: string | null;
  /** Up-front capacity check; undefined while loading (upload stays enabled — the API re-checks). */
  importCapacity?: ImportCapacity;
}

type CreationMethodOption = PickerCardOption<CreationMethod>;

const CreationMethodModal = ({
  isOpened,
  onClose,
  onManualCreate,
  onPptImport,
  onAiCreate,
  aiValidation,
  onContextFileRemoved,
  onAiValidationReset,
  isPptConversionEnabled,
  isCreateWithAiEnabled,
  isCreateLoading,
  isPptImporting,
  createError,
  pptError,
  importCapacity,
  initialAiValues,
}: CreationMethodModalProps): ReactElement => {
  const [selected, setSelected] = useState<CreationMethod | null>(
    initialAiValues ? CREATION_METHODS.AI : CREATION_METHODS.MANUAL,
  );
  const [name, setName] = useState(initialAiValues?.name ?? '');
  const [prompt, setPrompt] = useState(initialAiValues?.prompt ?? '');
  const [targetLength, setTargetLength] = useState<TargetLength | null>(
    initialAiValues?.targetLength ?? null,
  );
  const [nameStatus, setNameStatus] = useState<'default' | 'error'>('default');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  // Consent was given for these same files on the submit that failed.
  const [consentChecked, setConsentChecked] = useState(
    Boolean(initialAiValues),
  );
  const [contextFiles, setContextFiles] = useState<File[]>(
    initialAiValues?.contextFiles ?? [],
  );
  const [contextFileError, setContextFileError] = useState('');

  // Derived, so switching cards needs no extra reset bookkeeping.
  const cleanPrompt = stripHtml(prompt).trim();
  const isNameTooLong = name.trim().length > MAX_NAME_LENGTH;
  // Single source of truth for the Create gate: drives both the button's
  // disabled state and the submit guard, so they cannot drift apart.
  const isAiFormValid =
    name.trim().length > 0 &&
    !isNameTooLong &&
    cleanPrompt.length > 0 &&
    targetLength !== null &&
    consentChecked;

  // A result only applies while the prompt it was produced for is still the one
  // in the box; editing the prompt clears the message (and re-checks on next click).
  const isAiValidating = aiValidation?.isValidating ?? false;
  const promptStatus =
    aiValidation?.promptResult?.text === cleanPrompt
      ? aiValidation.promptResult.status
      : null;
  // Quota exhaustion sits under the prompt field too (wireframe B7, spec B7: the
  // guidance link stays below it). One message per field: quota wins over a stale
  // prompt result, since the server stopped before re-checking the prompt.
  const quotaMessage =
    aiValidation?.submitErrorKind === 'quota' ? aiValidation.submitError : null;
  const systemError =
    aiValidation?.submitErrorKind === 'system'
      ? aiValidation.submitError
      : null;
  const promptMessage =
    quotaMessage ??
    (promptStatus && promptStatus !== 'ok'
      ? PROMPT_MESSAGES[promptStatus]
      : '');
  const isRelevanceFlagged = (contextFile: File): boolean =>
    aiValidation?.fileStatuses[fileKey(contextFile)] === 'not_relevant';
  // True while the prompt in the box is the one the current verdicts were produced for.
  const isPromptUnchanged = aiValidation?.promptResult?.text === cleanPrompt;
  const contextFileMessage = (contextFile: File): string | undefined => {
    const status = aiValidation?.fileStatuses[fileKey(contextFile)];
    if (!status || status === 'ok' || status === 'accepted') return undefined;
    if (status === 'not_relevant')
      return notRelevantMessage(contextFile.name, isPromptUnchanged);
    return FILE_MESSAGES[status];
  };
  const contextFileTone = (contextFile: File): FileRowTone =>
    isRelevanceFlagged(contextFile) ? 'warning' : 'danger';
  // Hard failures disable Create (PO, 24 Sep): a failed prompt check or quota until the
  // prompt is edited (that clears the prompt verdict only), a blocked / unreadable /
  // too-long file until it is removed (file verdicts survive a prompt edit). A
  // relevance flag does not: clicking Create again IS the "use it anyway".
  // System errors (503, 413) do NOT block: retrying is the right response to those.
  const hasUnresolvedFailure =
    quotaMessage !== null ||
    (promptStatus !== null && promptStatus !== 'ok') ||
    contextFiles.some(
      (contextFile) =>
        contextFileMessage(contextFile) !== undefined &&
        !isRelevanceFlagged(contextFile),
    );
  const isAiCreateBlocked = isAiValidating || hasUnresolvedFailure;

  // LMS2-5271: these sit below the consent text, out of view on a full form, so a
  // concurrency block looked like a Create click that did nothing. Bring it in.
  const systemErrorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (systemError) {
      systemErrorRef.current?.scrollIntoView?.({
        block: 'nearest',
        behavior: 'smooth',
      });
    }
  }, [systemError]);

  useEffect(() => {
    if (!isOpened) {
      setSelected(CREATION_METHODS.MANUAL);
      setName('');
      setPrompt('');
      setTargetLength(null);
      setNameStatus('default');
      setFile(null);
      setFileError('');
      setConsentChecked(false);
      setContextFiles([]);
      setContextFileError('');
    }
  }, [isOpened]);

  // Snap to the PPT option only when a new error arrives (not on every render),
  // so the alert is visible but the user can still pick another method.
  useEffect(() => {
    if (pptError) {
      setSelected(CREATION_METHODS.PPT);
    }
  }, [pptError]);

  // Switching cards clears what belonged to the old one. Skipped on the first run so it
  // cannot wipe the values initialAiValues just seeded.
  const hasSwitchedCard = useRef(false);
  useEffect(() => {
    if (!hasSwitchedCard.current) {
      hasSwitchedCard.current = true;
      return;
    }
    setNameStatus('default');
    setFile(null);
    setFileError('');
    setConsentChecked(false);
    setContextFiles([]);
    setContextFileError('');
    // Files are gone, so their messages (and the draft they were checked against)
    // must go too — the next click starts a fresh attempt.
    onAiValidationReset?.();
  }, [selected, onAiValidationReset]);

  const options: CreationMethodOption[] = [
    {
      key: CREATION_METHODS.MANUAL,
      icon: <EditIcon />,
      title: 'Start from scratch',
      description: 'Build a module slide-by-slide in the editor.',
    },
    ...(isPptConversionEnabled
      ? [
          {
            key: CREATION_METHODS.PPT,
            icon: <FileIcon />,
            title: 'Import PowerPoint',
            description: "Upload a .pptx and we'll convert every slide.",
            badge: <AiPoweredBadge />,
          },
        ]
      : []),
    ...(isCreateWithAiEnabled
      ? [
          {
            key: CREATION_METHODS.AI,
            icon: <AiSparkleIcon />,
            title: 'Create with AI',
            description: "Describe a topic and we'll draft the module.",
            badge: <AiPoweredBadge />,
          },
        ]
      : []),
  ];

  // At the concurrent-import limit (or monthly cap): block the upload up-front.
  // undefined capacity (still loading) does not block — the API re-checks anyway.
  const isAtImportCapacity = importCapacity?.canStart === false;
  const capacityMessage =
    importCapacity?.reason === 'cap'
      ? 'Your organisation has reached its monthly AI usage limit, so new PowerPoint imports can’t start right now.'
      : `You already have ${importCapacity?.activeImports} imports in progress (limit ${importCapacity?.maxConcurrentImports}). Please wait for one to finish before starting another.`;

  // ── PPT file handlers ───────────────────────────────────────────────────────

  const filenameStem = (filename: string) => filename.replace(/\.[^/.]+$/, '');

  const handleDropAccepted = (acceptedFiles: any[]) => {
    const candidate = acceptedFiles[0] as File;
    setFileError('');
    if (!hasExtension(candidate, ['.pptx']) && candidate.type !== PPTX_MIME) {
      setFileError('Please select a .pptx file');
      return;
    }
    if (candidate.size > MAX_FILE_SIZE_BYTES) {
      setFileError(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
      return;
    }
    setFile(candidate);
    if (!name.trim()) {
      setName(filenameStem(candidate.name));
    }
  };

  const handleDropRejected = () => {
    setFileError(`Please select a .pptx file under ${MAX_FILE_SIZE_MB}MB`);
  };

  // LMS2-5271: each drop or pick calls onUploadStart, then onDropRejected (wrong
  // type or over 30MB), then onDropAccepted. The message is cleared at the start and
  // each handler adds its problem, so a mixed drop reports all of them.
  // FileUpload does not pass `disabled` to its dropzone, so drops while the checks
  // run are ignored here instead.
  const addContextFileError = (message: string) =>
    setContextFileError((prev) => (prev ? `${prev}. ${message}` : message));

  const handleContextUploadStart = () => {
    if (isAiValidating) return;
    setContextFileError('');
  };

  // Files are added in the order dropped while slots and the combined size allow.
  const handleContextDropAccepted = (acceptedFiles: any[]) => {
    if (isAiValidating) return;
    const added: File[] = [];
    let error = '';
    let totalBytes = contextFiles.reduce((sum, f) => sum + f.size, 0);
    (acceptedFiles as File[]).forEach((candidate) => {
      if (contextFiles.length + added.length >= MAX_CONTEXT_FILES) {
        error ||= `You can attach up to ${MAX_CONTEXT_FILES} files`;
        return;
      }
      if (totalBytes + candidate.size > MAX_TOTAL_CONTEXT_BYTES) {
        error ||= `Context files must total less than ${MAX_TOTAL_CONTEXT_MB}MB`;
        return;
      }
      added.push(candidate);
      totalBytes += candidate.size;
    });
    if (error) addContextFileError(error);
    if (added.length > 0) {
      setContextFiles([...contextFiles, ...added]);
      // Spec consent row: consent covers what is attached, so a new file needs it
      // given again. Deleting a file only narrows it, so removal leaves it ticked.
      setConsentChecked(false);
    }
  };

  // The upload rejects only for type or size, without saying which, so name each
  // reason found in the drop.
  const handleContextDropRejected = (rejectedFiles: any[]) => {
    if (isAiValidating) return;
    const files = rejectedFiles as File[];
    const isAcceptedType = (f: File) => hasExtension(f, CONTEXT_FILE_TYPES);
    if (files.some((f) => !isAcceptedType(f))) {
      addContextFileError(`Please select a ${CONTEXT_FILE_TYPE_LABEL} file`);
    }
    if (files.some((f) => isAcceptedType(f) && f.size > MAX_FILE_SIZE_BYTES)) {
      addContextFileError(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
    }
  };

  const removeContextFile = (index: number) => {
    const removed = contextFiles[index];
    const remaining = contextFiles.filter((_, i) => i !== index);
    setContextFiles(remaining);
    setContextFileError('');
    // Verdicts hang off the file's identity, so a document picked twice shares one
    // verdict. Forget it only when the last copy goes, or the surviving copy would
    // lose its message until the next click re-reports it.
    if (
      removed &&
      !remaining.some((other) => fileKey(other) === fileKey(removed))
    ) {
      onContextFileRemoved?.(removed);
    }
  };

  // ── Primary action ──────────────────────────────────────────────────────────

  const handlePrimaryAction = () => {
    if (!selected) return;

    if (selected === CREATION_METHODS.MANUAL) {
      if (!name.trim()) {
        setNameStatus('error');
        return;
      }
      onManualCreate(name.trim());
      return;
    }

    if (selected === CREATION_METHODS.PPT) {
      if (!name.trim() || !file || !consentChecked || isAtImportCapacity)
        return;
      onPptImport({ name: name.trim(), file });
      return;
    }

    if (selected === CREATION_METHODS.AI) {
      // `targetLength` is re-checked only to narrow it from `TargetLength | null`.
      if (!isAiFormValid || !targetLength || isAiCreateBlocked) return;
      onAiCreate({
        name: name.trim(),
        prompt: cleanPrompt,
        targetLength,
        contextFiles,
        // Second click on a still-flagged file = "use it anyway" (PO copy) — but only
        // when the prompt is the one the flag was judged against. An edited prompt is a
        // new question: the server re-judges the file, and the admin decides again.
        acceptContextFiles: isPromptUnchanged
          ? contextFiles.filter(isRelevanceFlagged)
          : [],
      });
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      e.key === 'Enter' &&
      selected === CREATION_METHODS.MANUAL &&
      name.trim()
    ) {
      handlePrimaryAction();
    }
  };

  // ── Button config ───────────────────────────────────────────────────────────

  const getPrimaryButton = () => {
    if (selected === CREATION_METHODS.MANUAL) {
      return (
        <Button
          type="primary"
          isUppercase={false}
          isDisabled={!name.trim() || isCreateLoading}
          isLoading={isCreateLoading}
          onClick={handlePrimaryAction}
        >
          Create
        </Button>
      );
    }

    if (selected === CREATION_METHODS.PPT) {
      const isReady =
        name.trim().length > 0 &&
        file !== null &&
        consentChecked &&
        !isAtImportCapacity;
      return (
        <Button
          type="primary"
          isUppercase={false}
          isDisabled={!isReady || isPptImporting}
          isLoading={isPptImporting}
          onClick={handlePrimaryAction}
        >
          Create
        </Button>
      );
    }

    if (selected === CREATION_METHODS.AI) {
      return (
        <Button
          type="primary"
          isUppercase={false}
          isDisabled={!isAiFormValid || isAiCreateBlocked}
          isLoading={isAiValidating}
          onClick={handlePrimaryAction}
        >
          Create
        </Button>
      );
    }

    return (
      <Button type="primary" isUppercase={false} isDisabled>
        Continue
      </Button>
    );
  };

  return (
    <Modal
      id="CreationMethodModal"
      className="new-module new-module--picker"
      type="large"
      isOpened={isOpened}
      title="New module"
      closeModal={onClose}
      primaryButton={getPrimaryButton()}
    >
      <Paragraph size="sm" color="gray" className="new-module__subtitle">
        Choose how you&apos;d like to start.
      </Paragraph>

      <div className="picker-grid">
        {options.map((option) => (
          <PickerCard
            key={option.key}
            option={option}
            isSelected={selected === option.key}
            onClick={setSelected}
          />
        ))}
      </div>

      {/* Manual: just module name */}
      {selected === CREATION_METHODS.MANUAL && (
        <Fieldset>
          <FormItem
            label="Module name"
            labelAddon={<span className="new-module__required">*</span>}
            status={nameStatus}
            message={nameStatus === 'error' ? 'Module name is required' : ''}
          >
            <Input
              id="input-new-module-name"
              value={name}
              onChange={(e: any) => {
                setName(e.target.value);
                setNameStatus('default');
              }}
              onKeyDown={handleKeyDown}
              placeholder="e.g. Workplace Safety Induction"
              autoComplete="off"
            />
          </FormItem>
          {createError && <Alert type="danger" message={createError} />}
        </Fieldset>
      )}

      {/* PPT: module name + file upload */}
      {selected === CREATION_METHODS.PPT && (
        <Fieldset>
          {isAtImportCapacity && (
            <Alert
              type="warning"
              id="import-capacity-alert"
              message={capacityMessage}
              className="mb-3"
            />
          )}
          <FormItem
            label="Module name"
            labelAddon={<span className="new-module__required">*</span>}
          >
            <Input
              id="input-ppt-import-name"
              value={name}
              onChange={(e: any) => setName(e.target.value)}
              placeholder="e.g. Workplace Safety Induction"
              autoComplete="off"
            />
          </FormItem>

          <FormItem
            label="PowerPoint file"
            labelAddon={<span className="new-module__required">*</span>}
          >
            {!file && (
              <FileUpload
                key={String(isOpened)}
                name="pptx-upload"
                displayDropArea
                displayFiles={false}
                isMulti={false}
                disabled={isAtImportCapacity}
                fileMaxSize={MAX_FILE_SIZE_MB}
                acceptedFiles={['.pptx']}
                dropareaText={
                  <>
                    <div className="pptx-droparea-icon-tile">
                      <UploadOutlinedIcon />
                    </div>
                    <span>{`Choose a .pptx or drag and drop (max ${MAX_FILE_SIZE_MB}MB, ${MAX_SLIDES} slides)`}</span>
                  </>
                }
                onDropAccepted={handleDropAccepted}
                onDropRejected={handleDropRejected}
              />
            )}

            {file && (
              <FileRow
                file={file}
                onRemove={() => {
                  setFile(null);
                  setFileError('');
                  setConsentChecked(false);
                }}
              />
            )}

            <div aria-live="assertive" aria-atomic="true">
              {fileError && <Alert type="danger" message={fileError} />}
              {pptError && <Alert type="danger" message={pptError} />}
            </div>
          </FormItem>

          {file && (
            <div className="ppt-consent">
              <Checkbox
                id="ppt-import-consent"
                ariaLabel="I confirm this file is appropriate for the workplace and free of offensive, unlawful, or otherwise unsuitable content. I understand this file's content, including any personal or financial information, will be processed to generate the module, and I'm responsible for ensuring it doesn't contain anything sensitive I don't want processed."
                isChecked={consentChecked}
                onChange={(e: any) => setConsentChecked(e.target.checked)}
              />
              <span className="ppt-consent__label">
                I confirm this file is appropriate for the workplace and free of
                offensive, unlawful, or otherwise unsuitable content. I
                understand this file&apos;s content, including any personal or
                financial information, will be processed to generate the module,
                and I&apos;m responsible for ensuring it doesn&apos;t contain
                anything sensitive I don&apos;t want processed.{' '}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <Link url={ACCEPTABLE_USE_URL} newWindow>
                  Acceptable use
                </Link>
              </span>
            </div>
          )}
        </Fieldset>
      )}
      {/* Create with AI: module name + prompt + target length + consent */}
      {selected === CREATION_METHODS.AI && (
        <Fieldset>
          <FormItem
            label="Module name"
            labelAddon={<span className="new-module__required">*</span>}
            status={isNameTooLong ? 'error' : 'default'}
            message={
              isNameTooLong
                ? `Maximum of ${MAX_NAME_LENGTH} characters only`
                : ''
            }
          >
            <Input
              id="input-ai-module-name"
              status={isNameTooLong ? 'error' : undefined}
              value={name}
              onChange={(e: any) => setName(e.target.value)}
              placeholder="e.g. Workplace Safety Induction"
              autoComplete="off"
            />
          </FormItem>

          <FormItem
            label="What should this module cover?"
            labelAddon={<span className="new-module__required">*</span>}
          >
            <TextArea
              name="aiPrompt"
              value={prompt}
              onChange={(e: any) => setPrompt(e.target.value)}
              rows={4}
              placeholder="Cover the learning objective, who it's for and their current knowledge level, and the tone to write in."
            />
            {promptMessage && (
              <div className="new-module__field-error" role="alert">
                {promptMessage}
              </div>
            )}
            <div className="new-module__field-hint">
              The more specific you are, the closer the draft will be.{' '}
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link url={PROMPT_HELP_URL} newWindow>
                What makes a good prompt?
              </Link>
            </div>
          </FormItem>

          <FormItem
            label="Length"
            labelAddon={<span className="new-module__required">*</span>}
          >
            <div className="new-module__field-note">
              Roughly how much content should the draft have? You can add or
              remove slides afterwards.
            </div>
            <div
              className="length-picker"
              role="radiogroup"
              aria-label="Length"
            >
              {TARGET_LENGTHS.map((option) => (
                <label
                  key={option.value}
                  htmlFor={`ai-length-${option.value}`}
                  className={`length-option${
                    targetLength === option.value
                      ? ' length-option--selected'
                      : ''
                  }`}
                >
                  <input
                    type="radio"
                    id={`ai-length-${option.value}`}
                    className="length-option__input"
                    name="ai-target-length"
                    value={option.value}
                    checked={targetLength === option.value}
                    onChange={() => setTargetLength(option.value)}
                  />
                  <span className="length-option__label">{option.label}</span>
                  <span className="length-option__hint">{option.hint}</span>
                </label>
              ))}
            </div>
          </FormItem>

          <FormItem label="Context files (optional)">
            {/* Spec A3: helper text states context-only, text-only, and both limits. */}
            <div className="new-module__field-note">
              {`Add up to ${MAX_CONTEXT_FILES} files (${CONTEXT_FILE_TYPE_LABEL}), each up to ${MAX_FILE_SIZE_MB}MB. PDF and PPTX files can be up to ${MAX_CONTEXT_FILE_PAGES} pages. Only their text is read — they inform what's generated and won't appear in the module.`}
            </div>

            {contextFiles.length < MAX_CONTEXT_FILES && (
              <FileUpload
                key={`ai-context-${isOpened}-${contextFiles.length}`}
                name="ai-context-upload"
                displayDropArea
                displayFiles={false}
                isMulti
                disabled={isAiValidating}
                fileMaxSize={MAX_FILE_SIZE_MB}
                acceptedFiles={CONTEXT_FILE_TYPES}
                onUploadStart={handleContextUploadStart}
                dropareaText={
                  <>
                    <div className="pptx-droparea-icon-tile">
                      <UploadOutlinedIcon />
                    </div>
                    <span>{`Choose a file or drag and drop (max ${MAX_FILE_SIZE_MB}MB)`}</span>
                  </>
                }
                onDropAccepted={handleContextDropAccepted}
                onDropRejected={handleContextDropRejected}
              />
            )}

            {contextFiles.length > 0 && (
              <div className="context-files">
                {contextFiles.map((contextFile, index) => (
                  <FileRow
                    key={`${contextFile.name}-${index}`}
                    file={contextFile}
                    removeLabel={`Remove ${contextFile.name}`}
                    onRemove={() => removeContextFile(index)}
                    message={contextFileMessage(contextFile)}
                    tone={contextFileTone(contextFile)}
                    isRemoveDisabled={isAiValidating}
                  />
                ))}
              </div>
            )}

            <div aria-live="assertive" aria-atomic="true">
              {contextFileError && (
                <Alert type="danger" message={contextFileError} />
              )}
            </div>
          </FormItem>

          <div className="ppt-consent">
            <Checkbox
              id="ai-create-consent"
              ariaLabel="I confirm this content is appropriate for the workplace and free of offensive, unlawful, or otherwise unsuitable content. I understand my prompt and any uploaded files, including any personal or financial information, will be processed to generate the module, and I'm responsible for ensuring they don't contain anything sensitive I don't want processed."
              isChecked={consentChecked}
              onChange={(e: any) => setConsentChecked(e.target.checked)}
            />
            <span className="ppt-consent__label">
              I confirm this content is appropriate for the workplace and free
              of offensive, unlawful, or otherwise unsuitable content. I
              understand my prompt and any uploaded files, including any
              personal or financial information, will be processed to generate
              the module, and I&apos;m responsible for ensuring they don&apos;t
              contain anything sensitive I don&apos;t want processed.{' '}
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <Link url={ACCEPTABLE_USE_URL} newWindow>
                Acceptable use
              </Link>
            </span>
          </div>

          {/* System failures (screening unavailable, upload too large) sit by the Create
              button; quota exhaustion renders under the prompt field instead. */}
          <div aria-live="assertive" aria-atomic="true" ref={systemErrorRef}>
            {systemError && (
              <Alert
                type="danger"
                id="ai-create-submit-error"
                message={systemError}
                className="mt-3"
              />
            )}
          </div>
        </Fieldset>
      )}
    </Modal>
  );
};

export default CreationMethodModal;
